import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import {
    arrayInsertAtIndex,
    notEmpty
} from '../common/util';

import {
    AVAILABLE_FUNCTIONS,
    CalculatedFunction,
    Expression,
    ExpressionSegment,
    ExpressionSegmentFactory,
    validateExpressionFactory
} from './calculated-output';
import * as jsep from 'jsep';
import { ExpressionInputMapping, ExpressionOutputMapping } from '@common/types';

const nbsp = '\xa0';

@Component({
    selector: 'calculated-output',
    templateUrl: 'calculated-output.component.html',
    styleUrls: ['./calculated-output.component.scss'],
})
export class CalculatedOutputComponent implements OnChanges {
    helpShowing = false;
    @Input() inputMappings: ExpressionInputMapping[] = [];
    @Input() outputMappings: ExpressionOutputMapping[] = [];
    @Output() expressionChange: EventEmitter<Expression> = new EventEmitter<Expression>();
    expressionSegments: ExpressionSegment[] = [ExpressionSegmentFactory.createTextSegment(nbsp)];
    @Input() disabled: boolean;

    @ViewChild('calculatedOutputField')
    private _calculatedOutputField: ElementRef;
    private _lastSegmentCursorPosition: any = null;
    private _expressionJson: string = null;

    readonly availableFunctions = AVAILABLE_FUNCTIONS;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.inputMappings) {
           this.updateInputExpressionSegmentNames();
        }

        if (changes.outputMappings) {
            this.updateOutputExpressionSegmentNames();
        }
    }

    /**
     * getter for the current cursor position
     */
    get segmentCursorPosition() {
        // getSelection() returns location of cursor in the text field via "anchorOffset"
        const selection = getSelection();

        if (selection && selection.anchorNode) {
            const segmentElement = selection.anchorNode.parentNode;
            const segmentIndex = this.expressionSegmentElementIndex(segmentElement);
            if (segmentIndex >= 0) {
                const cursorIndex = selection.anchorOffset;

                return { segmentIndex, cursorIndex, segmentElement };
            }
        }

        return null;
    }

    /**
     * Respond to a blur event on a segment.
     * @param event
     */
    segmentBlurred(event: Event): void {
        this.updateLastCursorPosition();

        const segmentIndex = this.expressionSegmentElementIndex(event.target);
        const segment = this.expressionSegments[segmentIndex];
        if (segment.isFreeformText) {
            const segmentElem = this._calculatedOutputField.nativeElement.children[segmentIndex];
            if (segment.text !== segmentElem.textContent) {
                segment.text = segmentElem.innerText;
                this.expressionChange.emit(this.expression);
            }
        }
    }

    @Input('expressionJson')
    get expressionJson(): string {
        return this._expressionJson;
    }

    set expressionJson(exprJson: string) {
        this._expressionJson = exprJson;
        if (exprJson) {
            // here we need to parse and reconstruct the expression segments. The
            // reconstruction is needed because we lose memeber functions passing
            // from JS to JSON
            const expr = JSON.parse(exprJson);
            this.expressionSegments = expr.expressionSegments.map(
                (seg: ExpressionSegment) => new ExpressionSegment(seg));
        } else {
            this.expressionSegments = [ExpressionSegmentFactory.createTextSegment(nbsp)];
        }
    }

    get expression(): Expression {
        let parsedExpression = null;
        let validationError = null;
        try {
            const exprStr = this.expressionString;
            parsedExpression = jsep(this.expressionString);
            if (exprStr) {
                validationError = this.validateParsedExpression(parsedExpression);
            }
        } catch (e) {
            let description = e.description;
            if (!description) {
                description = 'invalid expression format';
            }
            validationError = description;
        }

        return {
            parsedExpression,
            validationError,
            expressionSegments: this.expressionSegments,
        };
    }

    private validateParsedExpression(expressionTree: any): string {
        const validVariableSet = {};

        for (const inputMapping of this.inputMappings) {
            validVariableSet[inputMapping.ExpressionVariableName] = true;
        }

        for (const outputMapping of this.outputMappings) {
            validVariableSet[outputMapping.ExpressionVariableName] = true;
        }

        return validateExpressionFactory(expressionTree, validVariableSet);
    }

    /**
     * Function called when the user selects an input to insert into the expression
     * @param input
     */
    inputSelected(inputMapping: ExpressionInputMapping): void {
        this.insertSegments([
            ExpressionSegmentFactory.createInputSegment(inputMapping)
        ]);
    }

    /**
     * Function called when the user selects an output to insert into the expression
     * @param output
     */
    outputSelected(outputMapping: ExpressionOutputMapping): void {
        this.insertSegments([
            ExpressionSegmentFactory.createOutputSegment(outputMapping)
        ]);
    }

    /**
     * Called when user selects a function to insert into the expression
     * @param func
     */
    functionSelected(func: CalculatedFunction): void {
        this.insertSegments([
            ExpressionSegmentFactory.createFunctionSegments(func)
        ]);
    }

    /**
     * Save the current cursor position as the "last" position. This is used for inserting
     * variable segments.
     */
    updateLastCursorPosition(): void {
        const cursorPos = this.segmentCursorPosition;
        if (cursorPos) {
            this._lastSegmentCursorPosition = cursorPos;
        }
    }

    /**
     * Remove an existing variable (input or output) segment. This will cause flanking
     * free-text segments to merge.
     * @param segmentIndex the index of the segment to remove
     */
    removeVariableSegment(segmentIndex: number): void {
        // we'll remove the variable segment but then we also need to join
        // the surrounding freetext segments
        const deletedSegments = this.expressionSegments.splice(segmentIndex, 2);

        if (deletedSegments.length === 2 && segmentIndex >= 1) {
            const freetextToJoin = deletedSegments[1];
            const segmentToUpdate = this.expressionSegments[segmentIndex - 1];
            segmentToUpdate.text = this.normalizeFreetextWhitespace(
                segmentToUpdate.text +
                nbsp +
                freetextToJoin.text);
        }

        this._lastSegmentCursorPosition = null;
        this.expressionChange.emit(this.expression);
    }

    /**
     * In our component each expression segment corresponds to DOM element. This function
     * finds the index of the segment given that element.
     * @param element the DOM element for the segment
     */
    private expressionSegmentElementIndex(element: ParentNode | EventTarget): number {
        const childrenArr = Array.from(this._calculatedOutputField.nativeElement.children);
        return childrenArr.indexOf(element);
    }

    /**
     * Here we insert expression segments into the expression at
     * the last cursor position. This may result in splitting an existing free-freeform
     * text segment into two parts.
     * @param segments
     */
    private insertSegments(segments: ExpressionSegment[]): void {
        if (!notEmpty(segments)) {
            return;
        }

        if (this._lastSegmentCursorPosition) {
            // we insert based on the last place the cursor was by splitting
            // the relevant freetext segment
            const segmentIndex = this._lastSegmentCursorPosition.segmentIndex;
            const cursorIndex = this._lastSegmentCursorPosition.cursorIndex;
            if (segmentIndex >= 0) {
                const segmentToSplit = this.expressionSegments[segmentIndex];
                if (segmentToSplit && segmentToSplit.isFreeformText) {
                    const textToSplit = segmentToSplit.text;
                    const firstPart = textToSplit.substring(0, cursorIndex);
                    const secondPart = textToSplit.substring(cursorIndex);

                    // now update the segments with our split freetext and the new variable segment
                    segmentToSplit.text = firstPart;
                    const secondPartSegment = ExpressionSegmentFactory.createTextSegment(secondPart);

                    // insert new segments at index
                    const itemsToInsert = segments.slice();
                    itemsToInsert.push(secondPartSegment);
                    arrayInsertAtIndex(this.expressionSegments, segmentIndex + 1, itemsToInsert);
                }
            }
        } else {
            // if there was no "last cursor position" add it to the end of the expression
            for (const segment of segments) {
                this.expressionSegments.push(segment);
            }
            this.expressionSegments.push(
                ExpressionSegmentFactory.createTextSegment(nbsp)
            );
        }

        this.expressionSegments = this.mergeAdjacentTextSegments(this.expressionSegments);
        for (const currSegment of this.expressionSegments) {
            if (currSegment.isFreeformText) {
                currSegment.text = this.normalizeFreetextWhitespace(currSegment.text);
            }
        }

        this.expressionChange.emit(this.expression);
    }

    private mergeAdjacentTextSegments(segments: ExpressionSegment[]): ExpressionSegment[] {
        const mergedSegments: ExpressionSegment[] = [];
        for (const segment of segments) {
            let previousSegment: ExpressionSegment;
            if (mergedSegments.length > 0) {
                previousSegment = mergedSegments[mergedSegments.length - 1];
            }

            if (previousSegment &&
                previousSegment.isFreeformText &&
                segment.isFreeformText
            ) {
                previousSegment.text += ' ' + segment.text;
            } else {
                mergedSegments.push(segment);
            }

        }

        return mergedSegments;
    }

    /**
     * We concatenate (and transform) all expression segments to form
     * an expression string
     */
    private get expressionString(): string {
        let exprStr = '';
        for (const segment of this.expressionSegments) {
            exprStr += this.segmentToExpressionString(segment) + ' ';
        }

        exprStr = exprStr.replace(/\s+/g, ' ');
        exprStr = exprStr.trim();

        return exprStr;
    }

    /**
     * Convert the segment to a string that can be interpreted by the jsep
     * parser.
     * @param segment
     */
    private segmentToExpressionString(segment: ExpressionSegment): string {
        if (segment.isFreeformText) {
            return segment.text;
        } else {
            return segment.variableName;
        }
    }

    /**
     * for freetext replace any whitespace separation with a single nbsp
     * and perform trimming
     * @param text
     */
    private normalizeFreetextWhitespace(text: string): string {
        let newStr = text.replace(/\s+/g, nbsp);
        newStr = newStr.trim();
        if (!newStr.length) {
            newStr = nbsp;
        }

        return newStr;
    }

    private updateInputExpressionSegmentNames(): void {
        const segments = this.expressionSegments.filter(segment => this.inputMappings.some(inputExpresstion => inputExpresstion.C_Input_key === segment.key));
        for (const segment of segments) {
            const inputMapping = this.inputMappings.find(inputExpresstion => inputExpresstion.C_Input_key === segment.key);
            if (inputMapping) {
                segment.text = inputMapping.Input.InputName;
            }
        }
    }

    private updateOutputExpressionSegmentNames(): void {
        const segments = this.expressionSegments.filter(segment => this.outputMappings.some(outputExpression => outputExpression.C_Output_key === segment.key));
        for (const segment of segments) {
            const outputMapping = this.outputMappings.find(outputExpression => outputExpression.C_Output_key === segment.key);
            if (outputMapping) {
                segment.text = outputMapping.Output.OutputName;
            }
        }
    }
}
